import axiosInstance from "src/common/axiosInstance";
import { DEFAULT_COLUMNS } from "./components/Leads/LeadsTableColumns";

// 1. CACHE CONFIGURATION CONSTANTS
const CACHE = {
    WORKFLOW: {
        key: "lead_workflow-stages",
        ttl: 60 * 60 * 1000,
        endpoint: "/admin/pipelines/stages",
    },
    DEVELOPERS: {
        key: "dev-users-cache",
        ttl: 24 * 60 * 60 * 1000,
        endpoint: (workspaceId) => `/workspaces/${workspaceId}/users`,
    },
    LEADS: {
        key: "leadsData",
        ttl: 2 * 60 * 1000,
        endpoint: "/leads",
    },
    PIPELINES: {
        key: "pipelinesData",
        ttl: 60 * 60 * 1000,
        endpoint: "/admin/pipelines/pipelines",
    },
    SAVED_VIEWS: {
        key: "lead_saved_views",
    },
    COLUMNS: {
        key: "leads_table_columns_config",
    },
    LEAD_ACTIVITIES: {
        key: "lead_activities_data",
        ttl: 10 * 60 * 1000, // 10 minutes cache TTL
        endpoint: "/lead-activities",
    }
};

// 2. CORE CACHE UTILITIES
const setCacheItem = (cacheKey, data) => {
    try {
        localStorage.setItem(cacheKey, JSON.stringify({
            timestamp: Date.now(),
            data,
        }));
    } catch (error) {
        console.warn(`Failed to set cache for ${cacheKey}`, error);
    }
};

const getCacheItem = (cacheKey, ttl) => {
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;

    try {
        const parsed = JSON.parse(cached);
        if (ttl && (Date.now() - parsed.timestamp >= ttl)) {
            localStorage.removeItem(cacheKey);
            return null;
        }
        return parsed.data;
    } catch {
        localStorage.removeItem(cacheKey);
        return null;
    }
};

async function fetchWithCache(cacheKey, ttl, fetcher) {
    const cachedData = getCacheItem(cacheKey, ttl);
    if (cachedData) return cachedData;

    const data = await fetcher();
    setCacheItem(cacheKey, data);
    return data;
}

const clearCacheByPrefix = (prefix) => {
    Object.keys(localStorage).forEach(key => {
        if (key.startsWith(prefix)) {
            localStorage.removeItem(key);
        }
    });
};

// 3. PIPELINES API & CACHE
export const getPipelines = (workspaceIdOverride = "") => {
    const workspaceId = workspaceIdOverride || localStorage.getItem("autoappy_current_workspace_id") || "";
    const cacheKey = workspaceId ? `${CACHE.PIPELINES.key}_${workspaceId}` : CACHE.PIPELINES.key;

    return fetchWithCache(
        cacheKey,
        CACHE.PIPELINES.ttl,
        async () => {
            const headers = workspaceId ? { "X-Workspace-ID": workspaceId } : undefined;
            try {
                const { data } = await axiosInstance.get(CACHE.PIPELINES.endpoint, { headers });
                return data.data || [];
            } catch (error) {
                // The pipeline admin API is for admins; everyone else reads the
                // pipelines they can see from analytics (AUTO-CRM-32).
                if (error?.response?.status !== 403) throw error;
                const { data } = await axiosInstance.get("/analytics/crm-overview", { headers, params: { days: 7, mode: "queue" } });
                return (data?.data?.pipelines || []).map((pipeline) => ({ ...pipeline, status: "active" }));
            }
        }
    );
};

export const savePipelines = (data, workspaceIdOverride = "") => {
    const workspaceId = workspaceIdOverride || localStorage.getItem("autoappy_current_workspace_id") || "";
    setCacheItem(workspaceId ? `${CACHE.PIPELINES.key}_${workspaceId}` : CACHE.PIPELINES.key, data);
};

export const clearPipelines = () => clearCacheByPrefix(CACHE.PIPELINES.key);

// 4. WORKFLOW STAGES API & CACHE
export const getStages = (pipelineIdOverride = null) => {
    const pipelineId = pipelineIdOverride || localStorage.getItem("selectedPipelineId");

    if (!pipelineId) {
        return Promise.reject(
            new Error("selectedPipelineId is required to fetch stages.")
        );
    }

    const cacheKey = `${CACHE.WORKFLOW.key}_${pipelineId}`;

    return fetchWithCache(
        cacheKey,
        CACHE.WORKFLOW.ttl,
        async () => {
            const { data } = await axiosInstance.get(
                CACHE.WORKFLOW.endpoint,
                {
                    params: {
                        pipeline_id: pipelineId,
                    },
                }
            );

            if (!data?.success) {
                throw new Error("Unable to load pipeline stages.");
            }

            return data.data || [];
        }
    );
};

export const saveStages = (data) => {
    const pipelineId = localStorage.getItem("selectedPipelineId");

    if (pipelineId) {
        setCacheItem(
            `${CACHE.WORKFLOW.key}_${pipelineId}`,
            data
        );
    }
};

export const clearStages = () => {
    const pipelineId = localStorage.getItem("selectedPipelineId");

    if (pipelineId) {
        localStorage.removeItem(
            `${CACHE.WORKFLOW.key}_${pipelineId}`
        );
    }
};

export const clearAllStages = () => {
    clearCacheByPrefix(CACHE.WORKFLOW.key);
};

// 5. DEVELOPERS API & CACHE
export const getDevelopers = (workspaceId) => {
    if (!workspaceId) {
        return Promise.reject(new Error("workspaceId is required to fetch developers."));
    }
    return fetchWithCache(
        `${CACHE.DEVELOPERS.key}_${workspaceId}`,
        CACHE.DEVELOPERS.ttl,
        async () => {
            const { data } = await axiosInstance.get(CACHE.DEVELOPERS.endpoint(workspaceId));
            return data.data || [];
        }
    );
};
export const saveDevelopers = (workspaceId, data) => {
    if (workspaceId) setCacheItem(`${CACHE.DEVELOPERS.key}_${workspaceId}`, data);
};
export const clearDevelopers = () => clearCacheByPrefix(CACHE.DEVELOPERS.key);

// 6. LEADS API & CACHE
export const getLeadsPage = (params = {}, workspaceId = "") => {
    const workspaceCacheKey = workspaceId ? `_${workspaceId}` : "";
    const queryKey = JSON.stringify(Object.entries(params)
        .filter(([, value]) => value !== undefined && value !== null && value !== "")
        .sort(([left], [right]) => left.localeCompare(right)));
    const cacheKey = `${CACHE.LEADS.key}${workspaceCacheKey}_${queryKey}`;

    return fetchWithCache(
        cacheKey,
        CACHE.LEADS.ttl,
        async () => {
            const { data } = await axiosInstance.get(CACHE.LEADS.endpoint, {
                params,
                headers: workspaceId ? { "X-Workspace-ID": workspaceId } : undefined,
            });
            return data || { data: [], meta: {} };
        }
    );
};

export const getLeads = async (search = "", pipelineId = "", workspaceId = "") => {
    const response = await getLeadsPage({
        search: search || undefined,
        pipeline_id: pipelineId || undefined,
        per_page: 100,
    }, workspaceId);

    return Array.isArray(response) ? response : response?.data || [];
};
export const saveLeads = (data, search = "", pipelineId = "") => {
    const cacheKey = pipelineId || search
        ? `${CACHE.LEADS.key}_${pipelineId}${search ? `_${search}` : ""}`
        : CACHE.LEADS.key;
    setCacheItem(cacheKey, data);
};
export const clearLeads = () => clearCacheByPrefix(CACHE.LEADS.key);

// 7. SAVED VIEWS CONFIGURATION
export const getSavedViews = () => {
    const stored = localStorage.getItem(CACHE.SAVED_VIEWS.key);
    if (!stored) {
        saveSavedViews([]);
        return [];
    }
    try {
        return JSON.parse(stored);
    } catch {
        saveSavedViews([]);
        return [];
    }
};
export const saveSavedViews = (views) => {
    localStorage.setItem(CACHE.SAVED_VIEWS.key, JSON.stringify(views));
};
export const clearSavedViews = () => localStorage.removeItem(CACHE.SAVED_VIEWS.key);

// 8. TABLE COLUMNS CONFIGURATION
export const getSavedColumns = () => {
    const saved = localStorage.getItem(CACHE.COLUMNS.key);
    if (!saved) {
        saveColumns(DEFAULT_COLUMNS);
        return DEFAULT_COLUMNS;
    }

    try {
        const parsed = JSON.parse(saved);
        const restored = parsed
            .map(savedCol => {
                const col = DEFAULT_COLUMNS.find(c => c.id === savedCol.id);
                return col ? { ...col, visible: savedCol.visible } : null;
            })
            .filter(Boolean);

        const newCols = DEFAULT_COLUMNS.filter(c => !parsed.find(sc => sc.id === c.id));
        return [...restored, ...newCols];
    } catch {
        saveColumns(DEFAULT_COLUMNS);
        return DEFAULT_COLUMNS;
    }
};
export const saveColumns = (columns) => {
    const config = columns.map(col => ({ id: col.id, visible: col.visible }));
    localStorage.setItem(CACHE.COLUMNS.key, JSON.stringify(config));
};
export const resetColumns = () => {
    const columns = DEFAULT_COLUMNS.map(col => ({ ...col }));
    saveColumns(columns);
    return columns;
};
export const clearSavedColumns = () => localStorage.removeItem(CACHE.COLUMNS.key);

// 9. BULK CACHE CLEARING UTILITIES
export const clearPipelineAndLeadsCache = () => {
    Object.keys(localStorage).forEach(key => {
        if (
            key.startsWith(CACHE.LEADS.key) ||
            key.startsWith(CACHE.PIPELINES.key) ||
            key.startsWith(CACHE.DEVELOPERS.key)
        ) {
            localStorage.removeItem(key);
        }
    });
};

export const clearAllCacheExceptUserData = () => {
    const preservedKeys = new Set(["userData", "token"]);
    Object.keys(localStorage).forEach(key => {
        if (!preservedKeys.has(key)) {
            localStorage.removeItem(key);
        }
    });
};
export const getLeadActivities = () => fetchWithCache(
    CACHE.LEAD_ACTIVITIES.key,
    CACHE.LEAD_ACTIVITIES.ttl,
    async () => {
        const { data } = await axiosInstance.get(CACHE.LEAD_ACTIVITIES.endpoint);
        return data.data || [];
    }
);

// DELETE Lead Activity
export const deleteLeadActivity = async (id) => {
    const { data } = await axiosInstance.delete(
        `${CACHE.LEAD_ACTIVITIES.endpoint}/${id}`
    );
    // Invalidate local cache on delete
    clearLeadActivities();
    return data;
};

// Direct Cache Utilities for Lead Activities
export const saveLeadActivities = (data) => setCacheItem(CACHE.LEAD_ACTIVITIES.key, data);
export const clearLeadActivities = () => localStorage.removeItem(CACHE.LEAD_ACTIVITIES.key);
